import React, { useEffect, useCallback, useState } from "react";
import {
  Box,
  Paper,
  TableCell,
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
  TextField,
  CircularProgress,
  TablePagination,
  TableSortLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { useAdminOrdersContext } from "../AdminOrdersContext";
import { AdminModel } from "../../../models/AdminModel";
import { AdminOrdersTableRow } from "./AdminOrdersTableRow";

const readableSortColor = "rgba(0, 0, 0, 0.87)";
const readableSortIconColor = "rgba(0, 0, 0, 0.54)";
const sortLabelStyle: React.CSSProperties = { color: readableSortColor };

const useStyles = makeStyles({
  sortIcon: {
    color: `${readableSortIconColor} !important`,
  },
});

const materialUiV4PaginationHandlers = (
  onChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    nextPage: number
  ) => void,
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void
) =>
  ({
    // This app still runs on the pinned Material UI v4 API even though some type surfaces
    // in the workspace expect the newer pagination prop names.
    onChangePage,
    onChangeRowsPerPage,
  } as any);

export const AdminOrdersTable: React.FC = () => {
  const classes = useStyles();
  const [groupedOrders, setGroupedOrders] = useState<any[]>([]);
  const { isSendingInvoice, setIsSendingInvoice } = useAdminOrdersContext();
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("deliveryAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    setPage(0);
    return () => clearTimeout(handler);
  }, [search]);

  const reloadOrders = useCallback(() => {
    setLoading(true);
    AdminModel.getAllOrders({
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      sortBy,
      sortOrder,
    })
      .then((data: { orders?: any[]; total?: number }) => {
        setGroupedOrders(data.orders || []);
        setTotalCount(data.total || 0);
        setIsSendingInvoice(false);
      })
      .finally(() => setLoading(false));
  }, [debouncedSearch, page, rowsPerPage, sortBy, sortOrder, setIsSendingInvoice]);

  useEffect(() => {
    reloadOrders();
  }, [reloadOrders, isSendingInvoice]);

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    // Start each new ordering at the top so admins see the first matching page.
    setPage(0);

    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const sortLabelClasses = {
    icon: classes.sortIcon,
  };

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
        <TextField
          id="search-orders"
          label="Search Orders"
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 300 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="orders table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "createdAt"}
                  classes={sortLabelClasses}
                  direction={sortBy === "createdAt" ? sortOrder : "desc"}
                  onClick={() => handleSort("createdAt")}
                  style={sortLabelStyle}
                >
                  Date ordered
                </TableSortLabel>
              </TableCell>
              {/* 7 columns instead of the old 10: Name + Email are merged into
                  "Customer", the Notes button lives inside "Details", and
                  Created By Admin renders as a small tag under the order date.
                  Fewer, narrower columns is what keeps this table from
                  overflowing the page and scrolling sideways. */}
              <TableCell align="center">Customer</TableCell>
              <TableCell align="center">Details</TableCell>
              <TableCell align="center">Price</TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === "deliveryAt"}
                  classes={sortLabelClasses}
                  direction={sortBy === "deliveryAt" ? sortOrder : "desc"}
                  onClick={() => handleSort("deliveryAt")}
                  style={sortLabelStyle}
                >
                  Delivery Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}

            {!loading && groupedOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No Data Available
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              groupedOrders.map(order => {
                const notesList = (order.deliveryNotes || []).map((dn: any) => ({
                  status: order.deliveryStatus,
                  notes: dn.notes || "No Notes",
                  employeeId: dn.employeeId || "",
                  customerId: dn.customerId || "",
                  eventId: dn.eventId || "",
                  startTime: order.startTime ? new Date(order.startTime).toLocaleString() : undefined,
                  endTime: order.endTime ? new Date(order.endTime).toLocaleString() : undefined,
                }));

                return (
                  <AdminOrdersTableRow key={order._id} order={{ ...order, notesList }} reloadOrders={reloadOrders} />
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        {...materialUiV4PaginationHandlers(
          handleChangePage,
          handleChangeRowsPerPage
        )}
      />
    </>
  );
};